import React, { createContext, useContext, useMemo, useReducer } from 'react';
import { ReviewUrls, ServerReviewState } from '../utils/curationApi';
import {
  curationReviewReducer,
  initReviewState,
  ReviewAction,
  ReviewState,
} from './curationReviewReducer';

export interface FinishCurationPath {
  path: string;
  same_person: boolean;
}

interface CurationReviewContextValue {
  dispatch: React.Dispatch<ReviewAction>;
  experienceMax: number | null;
  experienceMin: number | null;
  finishCurationPath: FinishCurationPath | null;
  initialReview: ServerReviewState;
  rejectionReasons: ServerReviewState['rejectionReasons'];
  state: ReviewState;
  urls: ReviewUrls;
}

const CurationReviewContext = createContext<CurationReviewContextValue | null>(null);

interface Props {
  children: React.ReactNode;
  experienceMax: number | null;
  experienceMin: number | null;
  finishCurationPath?: FinishCurationPath | null;
  initialReview: ServerReviewState;
  urls: ReviewUrls;
}

export const CurationReviewProvider = ({
  children,
  experienceMax,
  experienceMin,
  finishCurationPath = null,
  initialReview,
  urls,
}: Props) => {
  const [state, dispatch] = useReducer(curationReviewReducer, initialReview, initReviewState);

  const value = useMemo(
    () => ({
      dispatch,
      experienceMax,
      experienceMin,
      finishCurationPath,
      initialReview,
      rejectionReasons: initialReview.rejectionReasons,
      state,
      urls,
    }),
    [state, urls, experienceMin, experienceMax, finishCurationPath, initialReview],
  );

  return <CurationReviewContext.Provider value={value}>{children}</CurationReviewContext.Provider>;
};

export const useCurationReview = (): CurationReviewContextValue => {
  const value = useContext(CurationReviewContext);
  if (!value) {
    throw new Error('useCurationReview must be used inside a CurationReviewProvider');
  }
  return value;
};
